import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/config/api";
import { useAuth } from "@/contexts/AuthContext";
import { showConfirm, showError } from "@/utils/dialog";
import { TablePagination } from "@/components/TablePagination";
import { addDaysToLocalDateInputValue, toLocalDateInputValue } from "@/utils/date";
import {
  Plus,
  TrendingUp,
  TrendingDown,
  Edit,
  Trash2,
  Search,
} from "lucide-react";

export default function IncomeExpenses() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState("income");
  const [incomeData, setIncomeData] = useState([]);
  const [expenseData, setExpenseData] = useState([]);
  const [ledgerData, setLedgerData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showIncomeModal, setShowIncomeModal] = useState(false);
  const [showExpenseModal, setShowExpenseModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [currency, setCurrency] = useState("OMR");
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [incomeForm, setIncomeForm] = useState({
    description: "",
    amount: "",
    category: "",
    date: toLocalDateInputValue(),
    invoice_id: "",
  });

  const [expenseForm, setExpenseForm] = useState({
    description: "",
    amount: "",
    category: "",
    date: toLocalDateInputValue(),
  });

  const [customIncomeCategory, setCustomIncomeCategory] = useState("");
  const [customExpenseCategory, setCustomExpenseCategory] = useState("");

  const canManage = user?.role === "finance" || user?.role === "admin";
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      if (activeTab === "income") {
        const data = await api.income.getAll();
        setIncomeData(data);
      } else if (activeTab === "expenses") {
        const data = await api.expenses.getAll();
        setExpenseData(data);
      } else {
        const data = await api.accounting.getLedgerActivity();
        setLedgerData(Array.isArray(data) ? data : []);
      }
    } catch (error) {
      console.error("Failed to fetch data:", error);
      setError("Failed to load data");
    } finally {
      setLoading(false);
    }
  }, [activeTab]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    api.settings.getCompany().then((s) => setCurrency(s?.currency || "OMR")).catch(() => {});
  }, []);

  const handleIncomeSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);

    // Use custom category if "Other Income" is selected
    const finalCategory =
      incomeForm.category === "Other Income" && customIncomeCategory.trim()
        ? customIncomeCategory.trim()
        : incomeForm.category;

    if (!finalCategory) {
      setError("Please select a category or enter a custom category");
      setSubmitting(false);
      return;
    }

    try {
      const incomeData = {
        ...incomeForm,
        category: finalCategory,
      };

      if (editingItem) {
        await api.income.update(editingItem.id, incomeData);
      } else {
        await api.income.create(incomeData);
      }
      setShowIncomeModal(false);
      setIncomeForm({
        description: "",
        amount: "",
        category: "",
        date: toLocalDateInputValue(),
        invoice_id: "",
      });
      setCustomIncomeCategory("");
      setEditingItem(null);
      fetchData();
    } catch (error) {
      console.error("Failed to save income:", error);
      setError(error.message || "Failed to save income entry");
    } finally {
      setSubmitting(false);
    }
  };

  const handleExpenseSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);

    // Use custom category if "Other Expenses" is selected
    const finalCategory =
      expenseForm.category === "Other Expenses" && customExpenseCategory.trim()
        ? customExpenseCategory.trim()
        : expenseForm.category;

    if (!finalCategory) {
      setError("Please select a category or enter a custom category");
      setSubmitting(false);
      return;
    }

    try {
      const expenseData = {
        ...expenseForm,
        category: finalCategory,
      };

      if (editingItem) {
        await api.expenses.update(editingItem.id, expenseData);
      } else {
        await api.expenses.create(expenseData);
      }
      setShowExpenseModal(false);
      setExpenseForm({
        description: "",
        amount: "",
        category: "",
        date: toLocalDateInputValue(),
      });
      setCustomExpenseCategory("");
      setEditingItem(null);
      fetchData();
    } catch (error) {
      console.error("Failed to save expense:", error);
      setError(error.message || "Failed to save expense entry");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id, type) => {
    const confirmed = await showConfirm(
      `Are you sure you want to delete this ${type} entry?`,
      "Delete Entry",
      "Delete",
    );
    if (!confirmed) {
      return;
    }

    try {
      if (type === "income") {
        await api.income.delete(id);
      } else {
        await api.expenses.delete(id);
      }
      fetchData();
    } catch (error) {
      console.error(`Failed to delete ${type}:`, error);
      await showError(`Failed to delete ${type} entry. Please try again.`);
    }
  };

  const openEditModal = (item, type) => {
    setEditingItem(item);
    if (type === "income") {
      const category = item.category || "";
      const isCustomCategory = !categories.includes(category);

      setIncomeForm({
        description: item.description || "",
        amount: item.amount || "",
        category: isCustomCategory ? "Other Income" : category,
        date: item.date || toLocalDateInputValue(),
        invoice_id: item.invoice_id || "",
      });
      setCustomIncomeCategory(isCustomCategory ? category : "");
      setShowIncomeModal(true);
    } else {
      const category = item.category || "";
      const isCustomCategory = !expenseCategories.includes(category);

      setExpenseForm({
        description: item.description || "",
        amount: item.amount || "",
        category: isCustomCategory ? "Other Expenses" : category,
        date: item.date || toLocalDateInputValue(),
      });
      setCustomExpenseCategory(isCustomCategory ? category : "");
      setShowExpenseModal(true);
    }
  };

  const getTotalIncome = () => {
    return incomeData.reduce(
      (sum, item) => sum + parseFloat(item.amount || 0),
      0
    );
  };

  const getTotalExpenses = () => {
    return expenseData.reduce(
      (sum, item) => sum + parseFloat(item.amount || 0),
      0
    );
  };

  const categories = [
    "Service Revenue",
    "Package Sales",
    "Visa Processing",
    "Ticket Sales",
    "Other Income",
  ];

  const expenseCategories = [
    "Office Rent",
    "Staff Salaries",
    "Marketing",
    "Travel Expenses",
    "Utilities",
    "Office Supplies",
    "Other Expenses",
  ];

  const currentCategories =
    activeTab === "income"
      ? categories
      : activeTab === "expenses"
        ? expenseCategories
        : [...new Set(ledgerData.map((i) => i.entry_type).filter(Boolean))];
  const currentRowsByTab =
    activeTab === "income"
      ? incomeData
      : activeTab === "expenses"
        ? expenseData
        : ledgerData;
  const filteredRows = useMemo(() => {
    let list = [...currentRowsByTab];
    const s = search.toLowerCase().trim();
    if (s) {
      list = list.filter(
        (item) =>
          (item.description || "").toLowerCase().includes(s) ||
          (item.category || "").toLowerCase().includes(s) ||
          (item.entry_type || "").toLowerCase().includes(s) ||
          (item.account_type || "").toLowerCase().includes(s) ||
          (item.invoice_id || "").toLowerCase().includes(s),
      );
    }
    if (categoryFilter) {
      if (activeTab === "ledger") {
        list = list.filter((item) => item.entry_type === categoryFilter);
      } else {
        list = list.filter((item) => item.category === categoryFilter);
      }
    }
    return list;
  }, [currentRowsByTab, search, categoryFilter, activeTab]);
  const totalPages = Math.max(1, Math.ceil(filteredRows.length / pageSize));
  const paginatedRows = filteredRows.slice((page - 1) * pageSize, page * pageSize);

  useEffect(() => {
    setPage(1);
    setSearch("");
    setCategoryFilter("");
  }, [activeTab]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center gap-3">
        <h1 className="text-2xl font-heading font-semibold text-secondary">
          Income & Expenses
        </h1>
        {canManage && (
          <div className="flex gap-2">
            <button
              onClick={() => {
                setEditingItem(null);
                setIncomeForm({
                  description: "",
                  amount: "",
                  category: "",
                  date: toLocalDateInputValue(),
                  invoice_id: "",
                });
                setCustomIncomeCategory("");
                setShowIncomeModal(true);
              }}
              className="bg-emerald-600 hover:bg-emerald-700 text-white font-medium rounded-lg px-4 py-2 shadow-sm hover:shadow-md transition-all flex items-center gap-2"
            >
              <Plus size={20} />
              Add Income
            </button>
            <button
              onClick={() => {
                setEditingItem(null);
                setExpenseForm({
                  description: "",
                  amount: "",
                  category: "",
                  date: toLocalDateInputValue(),
                });
                setCustomExpenseCategory("");
                setShowExpenseModal(true);
              }}
              className="bg-red-600 hover:bg-red-700 text-white font-medium rounded-lg px-4 py-2 shadow-sm hover:shadow-md transition-all flex items-center gap-2"
            >
              <Plus size={20} />
              Add Expense
            </button>
          </div>
        )}
        {!canManage && (
          <button
            type="button"
            onClick={() =>
              navigate(
                `/workflow-tickets?source_type=other&source_id=income-expenses&to_role=finance&title=Income%2FExpense%20action%20required&description=Please%20handle%20income%2Fexpense%20entry%20or%20adjustment.&due_date=${addDaysToLocalDateInputValue(1)}`,
              )
            }
            className="border border-indigo-300 text-indigo-700 bg-indigo-50 hover:bg-indigo-100 text-sm rounded-lg px-3 py-2"
          >
            Generate Finance Ticket
          </button>
        )}
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl border border-stone-200 shadow-sm p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-stone-600 mb-1">Total Income</p>
              <p className="text-3xl font-semibold text-emerald-600">
                {currency} {getTotalIncome().toFixed(2)}
              </p>
              <p className="form-helper mt-1">{incomeData.length} entries</p>
            </div>
            <div className="p-3 bg-emerald-100 rounded-lg">
              <TrendingUp size={24} className="text-emerald-600" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-stone-200 shadow-sm p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-stone-600 mb-1">Total Expenses</p>
              <p className="text-3xl font-semibold text-red-600">
                OMR {getTotalExpenses().toFixed(2)}
              </p>
              <p className="form-helper mt-1">{expenseData.length} entries</p>
            </div>
            <div className="p-3 bg-red-100 rounded-lg">
              <TrendingDown size={24} className="text-red-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-xl border border-stone-200 shadow-sm">
        <div className="flex border-b border-stone-200">
          <button
            onClick={() => setActiveTab("income")}
            className={`flex-1 px-6 py-4 font-medium transition-colors ${
              activeTab === "income"
                ? "text-emerald-600 border-b-2 border-emerald-600 bg-emerald-50"
                : "text-stone-600 hover:text-stone-900 hover:bg-stone-50"
            }`}
          >
            Income
          </button>
          <button
            onClick={() => setActiveTab("expenses")}
            className={`flex-1 px-6 py-4 font-medium transition-colors ${
              activeTab === "expenses"
                ? "text-red-600 border-b-2 border-red-600 bg-red-50"
                : "text-stone-600 hover:text-stone-900 hover:bg-stone-50"
            }`}
          >
            Expenses
          </button>
          <button
            onClick={() => setActiveTab("ledger")}
            className={`flex-1 px-6 py-4 font-medium transition-colors ${
              activeTab === "ledger"
                ? "text-cyan-700 border-b-2 border-cyan-600 bg-cyan-50"
                : "text-stone-600 hover:text-stone-900 hover:bg-stone-50"
            }`}
          >
            Auto Ledger Activity
          </button>
        </div>

        <div className="p-6">
          <div className="bg-stone-50 rounded-xl border border-stone-200 p-4 mb-4">
            <div className="flex flex-wrap gap-3 items-center">
              <div className="relative flex-1 min-w-[220px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                <input
                  type="text"
                  placeholder={
                    activeTab === "ledger"
                      ? "Search by description, entry type, account, or invoice..."
                      : "Search by description, category, or invoice..."
                  }
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(1);
                  }}
                  className="w-full pl-9 pr-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none"
                />
              </div>
              <select
                value={categoryFilter}
                onChange={(e) => {
                  setCategoryFilter(e.target.value);
                  setPage(1);
                }}
                className="px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none"
              >
                <option value="">
                  {activeTab === "ledger" ? "All entry types" : "All categories"}
                </option>
                {currentCategories.map((category) => (
                  <option key={category} value={category}>
                    {category}
                  </option>
                ))}
              </select>
            </div>
            <p className="form-helper mt-2">
              Showing {filteredRows.length} of {currentRowsByTab.length}{" "}
              {activeTab === "income"
                ? "income"
                : activeTab === "expenses"
                  ? "expense"
                  : "ledger"}{" "}
              record(s)
            </p>
          </div>
          {/* Income Table */}
          {activeTab === "income" && (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-stone-50 border-b border-stone-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-stone-600 uppercase">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-stone-600 uppercase">
                      Description
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-stone-600 uppercase">
                      Category
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-stone-600 uppercase">
                      Amount
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-stone-600 uppercase">
                      Recorded By
                    </th>
                    {canManage && (
                      <th className="px-6 py-3 text-left text-xs font-medium text-stone-600 uppercase">
                        Actions
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-200">
                  {paginatedRows.length === 0 ? (
                    <tr>
                      <td
                        colSpan={canManage ? 6 : 5}
                        className="px-6 py-8 text-center text-stone-500"
                      >
                        {incomeData.length === 0 ? "No income entries found" : "No income entries match your filters."}
                      </td>
                    </tr>
                  ) : (
                    paginatedRows.map((item) => (
                      <tr
                        key={item.id}
                        className="hover:bg-stone-50 transition-colors"
                      >
                        <td className="px-6 py-4 text-sm text-stone-600">
                          {new Date(item.date).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 text-sm text-stone-700">
                          {item.description}
                        </td>
                        <td className="px-6 py-4 text-sm text-stone-600">
                          {item.category}
                        </td>
                        <td className="px-6 py-4 text-right font-mono text-sm font-semibold text-emerald-600">
                          {currency} {parseFloat(item.amount).toFixed(2)}
                        </td>
                        <td className="px-6 py-4 text-sm text-stone-700">
                          {item.created_by_name || "-"}
                          {item.created_by_role ? ` (${item.created_by_role})` : ""}
                        </td>
                        {canManage && (
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => openEditModal(item, "income")}
                                className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                title="Edit"
                              >
                                <Edit size={16} />
                              </button>
                              <button
                                onClick={() => handleDelete(item.id, "income")}
                                className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                title="Delete"
                              >
                                <Trash2 size={16} />
                              </button>
                            </div>
                          </td>
                        )}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* Expenses Table */}
          {activeTab === "expenses" && (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-stone-50 border-b border-stone-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-stone-600 uppercase">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-stone-600 uppercase">
                      Description
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-stone-600 uppercase">
                      Category
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-stone-600 uppercase">
                      Amount
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-stone-600 uppercase">
                      Recorded By
                    </th>
                    {canManage && (
                      <th className="px-6 py-3 text-left text-xs font-medium text-stone-600 uppercase">
                        Actions
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-200">
                  {paginatedRows.length === 0 ? (
                    <tr>
                      <td
                        colSpan={canManage ? 6 : 5}
                        className="px-6 py-8 text-center text-stone-500"
                      >
                        {expenseData.length === 0 ? "No expense entries found" : "No expense entries match your filters."}
                      </td>
                    </tr>
                  ) : (
                    paginatedRows.map((item) => (
                      <tr
                        key={item.id}
                        className="hover:bg-stone-50 transition-colors"
                      >
                        <td className="px-6 py-4 text-sm text-stone-600">
                          {new Date(item.date).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 text-sm text-stone-700">
                          {item.description}
                        </td>
                        <td className="px-6 py-4 text-sm text-stone-600">
                          {item.category}
                        </td>
                        <td className="px-6 py-4 text-right font-mono text-sm font-semibold text-red-600">
                          {currency} {parseFloat(item.amount).toFixed(2)}
                        </td>
                        <td className="px-6 py-4 text-sm text-stone-700">
                          {item.created_by_name || "-"}
                          {item.created_by_role ? ` (${item.created_by_role})` : ""}
                        </td>
                        {canManage && (
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => openEditModal(item, "expenses")}
                                className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                title="Edit"
                              >
                                <Edit size={16} />
                              </button>
                              <button
                                onClick={() =>
                                  handleDelete(item.id, "expenses")
                                }
                                className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                title="Delete"
                              >
                                <Trash2 size={16} />
                              </button>
                            </div>
                          </td>
                        )}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
          {activeTab === "ledger" && (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-stone-50 border-b border-stone-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-stone-600 uppercase">Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-stone-600 uppercase">Entry Type</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-stone-600 uppercase">Account</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-stone-600 uppercase">Amount</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-stone-600 uppercase">Description</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-stone-600 uppercase">Reference</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-200">
                  {paginatedRows.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-6 py-8 text-center text-stone-500">
                        {ledgerData.length === 0
                          ? "No auto ledger entries found."
                          : "No ledger entries match your filters."}
                      </td>
                    </tr>
                  ) : (
                    paginatedRows.map((item) => (
                      <tr key={item.id} className="hover:bg-stone-50 transition-colors">
                        <td className="px-6 py-4 text-sm text-stone-600">
                          {new Date(item.entry_date || item.created_at).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 text-sm text-stone-700">{item.entry_type}</td>
                        <td className="px-6 py-4 text-sm text-stone-700">{item.account_type}</td>
                        <td className="px-6 py-4 text-right font-mono text-sm font-semibold text-stone-800">
                          {item.currency || currency} {parseFloat(item.amount || 0).toFixed(2)}
                        </td>
                        <td className="px-6 py-4 text-sm text-stone-700">{item.description || "-"}</td>
                        <td className="px-6 py-4 text-xs text-stone-500">
                          {item.invoice_id ? `Inv: ${item.invoice_id.slice(0, 8)}` : ""}
                          {item.payment_id ? `${item.invoice_id ? " • " : ""}Pay: ${item.payment_id.slice(0, 8)}` : ""}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
          <TablePagination
            page={page}
            totalPages={totalPages}
            onPageChange={setPage}
            totalItems={filteredRows.length}
            pageSize={pageSize}
            onPageSizeChange={(n) => {
              setPageSize(n);
              setPage(1);
            }}
          />
        </div>
      </div>

      {/* Income Modal */}
      {showIncomeModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
            <div className="p-6 border-b border-stone-200">
              <h2 className="text-xl font-semibold text-secondary">
                {editingItem ? "Edit Income" : "Add Income Entry"}
              </h2>
            </div>
            <form onSubmit={handleIncomeSubmit} className="p-6 space-y-4">
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                  {error}
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">
                  Description <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={incomeForm.description}
                  onChange={(e) =>
                    setIncomeForm({
                      ...incomeForm,
                      description: e.target.value,
                    })
                  }
                  required
                  className="w-full px-4 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">
                    Amount <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={incomeForm.amount}
                    onChange={(e) =>
                      setIncomeForm({ ...incomeForm, amount: e.target.value })
                    }
                    required
                    className="w-full px-4 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">
                    Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={incomeForm.date}
                    onChange={(e) =>
                      setIncomeForm({ ...incomeForm, date: e.target.value })
                    }
                    required
                    className="w-full px-4 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">
                  Category <span className="text-red-500">*</span>
                </label>
                <select
                  value={incomeForm.category}
                  onChange={(e) => {
                    setIncomeForm({ ...incomeForm, category: e.target.value });
                    if (e.target.value !== "Other Income") {
                      setCustomIncomeCategory("");
                    }
                  }}
                  required
                  className="w-full px-4 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none"
                >
                  <option value="">Select Category</option>
                  {categories.map((cat) => (
                    <option key={cat} value={cat}>
                      {cat}
                    </option>
                  ))}
                </select>
              </div>
              {incomeForm.category === "Other Income" && (
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">
                    Enter Custom Category{" "}
                    <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={customIncomeCategory}
                    onChange={(e) => setCustomIncomeCategory(e.target.value)}
                    placeholder="Enter custom category name"
                    required={incomeForm.category === "Other Income"}
                    className="w-full px-4 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none"
                  />
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">
                  Invoice ID (Optional)
                </label>
                <input
                  type="text"
                  value={incomeForm.invoice_id}
                  onChange={(e) =>
                    setIncomeForm({ ...incomeForm, invoice_id: e.target.value })
                  }
                  className="w-full px-4 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none"
                  placeholder="Link to invoice (optional)"
                />
              </div>
              <div className="flex gap-4 justify-end pt-4 border-t border-stone-200">
                <button
                  type="button"
                  onClick={() => {
                    setShowIncomeModal(false);
                    setEditingItem(null);
                    setError("");
                    setCustomIncomeCategory("");
                  }}
                  className="px-6 py-2 border border-stone-300 text-stone-700 rounded-lg hover:bg-stone-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-6 py-2 bg-emerald-600 hover:bg-emerald-700 text-white font-medium rounded-lg shadow-sm hover:shadow-md transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting
                    ? "Saving..."
                    : editingItem
                    ? "Update"
                    : "Add Income"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Expense Modal */}
      {showExpenseModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
            <div className="p-6 border-b border-stone-200">
              <h2 className="text-xl font-semibold text-secondary">
                {editingItem ? "Edit Expense" : "Add Expense Entry"}
              </h2>
            </div>
            <form onSubmit={handleExpenseSubmit} className="p-6 space-y-4">
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                  {error}
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">
                  Description <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={expenseForm.description}
                  onChange={(e) =>
                    setExpenseForm({
                      ...expenseForm,
                      description: e.target.value,
                    })
                  }
                  required
                  className="w-full px-4 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">
                    Amount <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={expenseForm.amount}
                    onChange={(e) =>
                      setExpenseForm({ ...expenseForm, amount: e.target.value })
                    }
                    required
                    className="w-full px-4 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">
                    Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={expenseForm.date}
                    onChange={(e) =>
                      setExpenseForm({ ...expenseForm, date: e.target.value })
                    }
                    required
                    className="w-full px-4 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">
                  Category <span className="text-red-500">*</span>
                </label>
                <select
                  value={expenseForm.category}
                  onChange={(e) => {
                    setExpenseForm({
                      ...expenseForm,
                      category: e.target.value,
                    });
                    if (e.target.value !== "Other Expenses") {
                      setCustomExpenseCategory("");
                    }
                  }}
                  required
                  className="w-full px-4 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none"
                >
                  <option value="">Select Category</option>
                  {expenseCategories.map((cat) => (
                    <option key={cat} value={cat}>
                      {cat}
                    </option>
                  ))}
                </select>
              </div>
              {expenseForm.category === "Other Expenses" && (
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">
                    Enter Custom Category{" "}
                    <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={customExpenseCategory}
                    onChange={(e) => setCustomExpenseCategory(e.target.value)}
                    placeholder="Enter custom category name"
                    required={expenseForm.category === "Other Expenses"}
                    className="w-full px-4 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none"
                  />
                </div>
              )}
              <div className="flex gap-4 justify-end pt-4 border-t border-stone-200">
                <button
                  type="button"
                  onClick={() => {
                    setShowExpenseModal(false);
                    setEditingItem(null);
                    setError("");
                    setCustomExpenseCategory("");
                  }}
                  className="px-6 py-2 border border-stone-300 text-stone-700 rounded-lg hover:bg-stone-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-6 py-2 bg-red-600 hover:bg-red-700 text-white font-medium rounded-lg shadow-sm hover:shadow-md transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting
                    ? "Saving..."
                    : editingItem
                    ? "Update"
                    : "Add Expense"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
